import styled, { css } from 'styled-components';
import { Button, Icon, Loader } from 'src/components';
import { Content as ButtonContent } from 'src/components/Button/styles';
export const Container = styled.section<{ $hasMobileNavigation: boolean }>(
  ({ theme, $hasMobileNavigation }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    isolation: isolate;
    height: calc(100dvh - ${theme.sz.s18});
    min-height: ${theme.sz.s64};
    width: calc(100% + 2 * ${theme.sz.s8});
    max-width: none;
    margin: -${theme.sz.s2} -${theme.sz.s8} -${theme.sz.s12};
    background-color: ${theme.co.blobBackground};
    color: ${theme.co.neutral10};

    @media (max-width: ${theme.sz.maxMobileBreakpoint}) {
      height: calc(100dvh - ${theme.sz.s18} - ${$hasMobileNavigation ? theme.sz.s16 : '0px'});
    }

    @media (max-width: ${theme.sz.md}) {
      width: calc(100% + 2 * ${theme.sz.s4});
      margin: -${theme.sz.s2} -${theme.sz.s4} -${theme.sz.s7};
    }
  `,
);

export const Chrome = styled.div<{ $visible: boolean }>(
  ({ theme, $visible }) => css`
    position: relative;
    z-index: ${theme.zi.z2};
    flex-shrink: 0;
    opacity: ${$visible ? 1 : 0};
    pointer-events: ${$visible ? 'auto' : 'none'};
    transition: opacity 200ms ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
);

export const ScrollArea = styled.div(
  ({ theme }) => css`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-anchor: none;
    overscroll-behavior-y: contain;
    scrollbar-width: thin;
    scrollbar-gutter: stable both-edges;
    scrollbar-color: ${theme.co.neutral90} transparent;
    scroll-padding-block: ${theme.sz.s8};
    ${theme.focusVisible};
  `,
);

export const Path = styled.ol<{ $editLayout?: boolean }>(
  ({ theme, $editLayout }) => css`
    position: relative;
    width: 100%;
    max-width: ${$editLayout ? theme.sz.xl : theme.sz.lg};
    margin: 0 auto;
    padding: ${theme.sz.s4} ${theme.sz.s4} max(${theme.sz.s16}, calc(50dvh - ${theme.sz.s16}));
    padding-block-start: max(${theme.sz.s4}, var(--blob-path-leading-space, 0px));
    list-style: none;

    @media (max-width: ${theme.sz.md}) {
      padding-inline: ${theme.sz.s2};
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
);

export const Row = styled.li<{ $current: boolean; $editLayout?: boolean; $hidden?: boolean }>(
  ({ theme, $current, $editLayout, $hidden }) => css`
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: ${
      $current && !$editLayout
        ? `minmax(${theme.sz.s4}, 1fr) auto minmax(${theme.sz.s4}, 1fr)`
        : 'auto'
    };
    align-items: center;
    width: 100%;
    max-width: none;
    min-height: ${
      $current && !$editLayout ? theme.sz.s74 : $editLayout ? theme.sz.s20 : theme.sz.s40
    };
    padding-block: ${$current && !$editLayout ? '0' : theme.sz.s4};
    opacity: ${$hidden ? 0 : 1};
    max-height: ${$hidden ? '0px' : '2000px'};
    margin: 0;
    overflow: ${$hidden ? 'hidden' : 'visible'};
    pointer-events: ${$hidden ? 'none' : 'auto'};

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
);

export const Connector = styled.svg<{ $visible?: boolean }>(
  ({ theme, $visible = true }) => css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    z-index: 0;
    pointer-events: none;
    color: ${theme.co.neutral90};
    opacity: ${$visible ? 1 : 0};
    transition: opacity 200ms ease;

    path[data-completed='true'] {
      stroke: ${theme.co.primary40};
    }

    [dir='rtl'] & {
      transform: scaleX(-1);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
);

export const CurrentConnector = styled(Connector).attrs<{
  $below: boolean;
  $top?: number;
  $height: number;
}>(({ $top = 0, $height }) => ({ style: { top: $top, height: $height } }))(
  ({ $below }) => css`
    grid-column: 1 / -1;
    grid-row: ${$below ? '3 / 4' : '1 / 2'};
    bottom: auto;
  `,
);

const resetButton = css`
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: pointer;
`;

export const CurrentCard = styled.button<{ $editLayout?: boolean }>(
  ({ theme, $editLayout }) => css`
    ${resetButton};
    position: relative;
    grid-column: 1;
    grid-row: ${$editLayout ? 'auto' : 2};
    z-index: 1;
    justify-self: ${$editLayout ? 'stretch' : 'center'};
    display: flex;
    flex-direction: ${$editLayout ? 'row' : 'column'};
    align-items: ${$editLayout ? 'center' : 'center'};
    justify-content: ${$editLayout ? 'flex-start' : 'center'};
    gap: ${$editLayout ? theme.sz.s3 : theme.sz.s2};
    width: ${$editLayout ? '100%' : `min(72%, ${theme.sz.s64})`};
    max-width: ${$editLayout ? 'none' : `min(72%, ${theme.sz.s64})`};
    min-height: ${$editLayout ? theme.sz.s20 : 'auto'};
    padding: ${$editLayout ? `${theme.sz.s3} ${theme.sz.s4}` : theme.sz.s3};
    border: ${theme.bo.mediumWidth} solid ${theme.co.primary20};
    border-radius: ${$editLayout ? theme.sz.s4 : theme.sz.s5};
    background-color: ${theme.co.neutral99};
    color: ${theme.co.neutral10};
    text-align: ${$editLayout ? 'start' : 'center'};
    text-decoration: none;
    box-shadow: ${
      $editLayout
        ? `0 ${theme.sz.s05} ${theme.sz.s1} ${theme.co.neutral10}0A, 0 ${theme.sz.s1} ${theme.sz.s4} ${theme.co.neutral10}0F`
        : `0 ${theme.sz.s3} ${theme.sz.s8} ${theme.co.neutral10}14`
    };
    overflow: hidden;
    ${theme.focusVisible};

    &:hover {
      border-color: ${theme.co.primary0};
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
);

export const CurrentMedia = styled.div<{ $editLayout?: boolean }>(
  ({ theme, $editLayout }) => css`
    flex-shrink: 0;
    width: ${$editLayout ? theme.sz.s12 : '100%'};
    max-width: ${$editLayout ? theme.sz.s12 : '100%'};
    aspect-ratio: 1;
    overflow: hidden;
    border-radius: ${theme.sz.s3};

    img,
    video,
    & > div {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
);

export const CurrentCopy = styled.div<{ $editLayout?: boolean }>(
  ({ $editLayout }) => css`
    flex: ${$editLayout ? '1' : '0 1 auto'};
    min-width: 0;
    text-align: inherit;
  `,
);

export const Badge = styled.span<{ $hidden?: boolean }>(
  ({ theme, $hidden }) => css`
    ${theme.typography.paragraph.s};
    max-width: ${$hidden ? '0px' : '100%'};
    max-height: ${$hidden ? '0px' : '4rem'};
    margin: 0;
    padding: ${$hidden ? '0' : `${theme.sz.s05} ${theme.sz.s3}`};
    overflow: hidden;
    opacity: ${$hidden ? 0 : 1};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary30};
    color: ${theme.co.neutral10};
    font-weight: 700;
    white-space: nowrap;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
);

export const Title = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    font-weight: 700;
    overflow-wrap: anywhere;
  `,
);

export const Progress = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    display: block;
    color: ${theme.co.neutral40};
    overflow-wrap: anywhere;
  `,
);

type BlobLayoutProps = {
  $position: number;
  $labelBefore: boolean;
  $editLayout?: boolean;
  $current?: boolean;
};

/**
 * Leading spacer that centres the blob on the dotted path.
 * Width is position% minus half the blob and the flex gap before it —
 * do not clamp with max-width, or right-side (70%) blobs sit too far left.
 */
export const BlobSpacer = styled.span<{ $position: number; $editLayout?: boolean }>(
  ({ theme, $position, $editLayout }) => css`
    flex: 0 0 auto;
    width: ${
      $editLayout
        ? theme.sz.s4
        : `max(0px, calc(${$position}% - (${theme.sz.s16} / 2) - ${theme.sz.s4}))`
    };
  `,
);

export const BlobButton = styled.button<BlobLayoutProps>(
  ({ theme, $position, $labelBefore, $editLayout, $current }) => css`
    ${resetButton};
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: ${$editLayout ? theme.sz.s3 : theme.sz.s4};
    width: 100%;
    max-width: none;
    min-height: ${theme.sz.s20};
    padding-block: ${$editLayout ? theme.sz.s3 : 0};
    padding-inline-end: ${$editLayout ? theme.sz.s4 : 0};
    color: ${theme.co.neutral10};
    text-decoration: none;
    border-radius: ${theme.sz.s4};
    border: ${theme.bo.mediumWidth} solid
      ${$editLayout ? ($current ? theme.co.primary20 : theme.co.neutral90) : 'transparent'};
    background-color: ${$editLayout ? theme.co.neutral99 : 'transparent'};
    box-shadow: ${
      $editLayout
        ? `0 ${theme.sz.s05} ${theme.sz.s1} ${theme.co.neutral10}0A, 0 ${theme.sz.s1} ${theme.sz.s4} ${theme.co.neutral10}0F`
        : 'none'
    };
    ${theme.focusVisible};

    & > div:last-child {
      flex: 1;
      min-width: 0;
      padding-inline: ${$editLayout ? '0' : theme.sz.s2};
      text-align: ${$editLayout || !$labelBefore ? 'start' : 'end'};
      ${
        !$editLayout &&
        $labelBefore &&
        css`
          position: absolute;
          inset-inline-end: calc(100% - ${$position}% + ${theme.sz.s8});
          width: min(42%, calc(${$position}% - ${theme.sz.s10}));
          flex: 0 0 auto;
        `
      };

      @media (max-width: ${theme.sz.lg}) {
        padding-inline: ${$editLayout ? '0' : theme.sz.s2};
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;

      & > div:last-child {
        transition: none;
      }
    }

    &:hover ${Title} {
      text-decoration: underline;
      text-underline-offset: ${theme.sz.s1};
    }
  `,
);

export const Blob = styled.div<{ $completed: boolean; $skipped?: boolean; $editLayout?: boolean }>(
  ({ theme, $completed, $skipped, $editLayout }) => css`
    position: relative;
    flex: 0 0 auto;
    width: ${$editLayout ? theme.sz.s12 : theme.sz.s16};
    height: ${$editLayout ? theme.sz.s12 : theme.sz.s16};
    display: grid;
    place-items: center;
    border: ${theme.bo.thickWidth} ${$skipped ? 'dashed' : 'solid'}
      ${$completed ? theme.co.primary30 : $skipped ? theme.co.neutral70 : theme.co.neutral95};
    border-radius: ${$editLayout ? theme.sz.s3 : '46% 54% 48% 52% / 54% 46% 54% 46%'};
    background-color: ${$completed ? theme.co.primary95 : theme.co.neutral95};
    color: ${theme.co.neutral50};
    font-size: ${theme.sz.s6};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
      filter: ${$completed ? 'none' : 'grayscale(1)'};
      opacity: ${$completed ? 1 : 0.7};
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
);

export const StatusLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    display: block;
    color: ${theme.co.neutral40};
    font-style: italic;
  `,
);

export const PageButtonRow = styled.li(
  ({ theme }) => css`
    display: flex;
    justify-content: center;
    padding-block: ${theme.sz.s4};
  `,
);

export const CompletedBadge = styled.span(
  ({ theme }) => css`
    position: absolute;
    inset-inline-end: -${theme.sz.s1};
    bottom: -${theme.sz.s1};
    display: grid;
    place-items: center;
    width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    border: ${theme.bo.mediumWidth} solid ${theme.co.blobBackground};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary30};
    color: ${theme.co.neutral10};
    font-size: ${theme.sz.s3};
  `,
);

export const FooterContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  z-index: 1;
`;

export const FloatingEditControl = styled.div(
  ({ theme }) => css`
    display: none;

    @media (max-width: ${theme.sz.maxMobileBreakpoint}) {
      display: block;
      position: absolute;
      right: ${theme.sz.s4};
      bottom: calc(100% + ${theme.sz.s4});
      z-index: 2;
    }
  `,
);

export const Footer = styled.div(
  ({ theme }) => css`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s2};
    padding: ${theme.sz.s4} ${theme.sz.s6} max(${theme.sz.s5}, env(safe-area-inset-bottom));
    background: linear-gradient(to bottom, transparent, ${theme.co.blobBackground} ${theme.sz.s4});
    overflow: visible;

    & > button {
      width: 100%;
      max-width: ${theme.sz.s};
    }
  `,
);

/** Desktop-only landmark: above the footer fade, below the Learn button. */
export const LanguageLandmark = styled.div<{ $src: string }>(
  ({ theme, $src }) => css`
    display: none;

    @media (min-width: calc(${theme.sz.maxMobileBreakpoint} + 0.01rem)) {
      display: block;
      position: absolute;
      right: 0;
      bottom: 0;
      z-index: 1;
      width: ${theme.sz.lg};
      height: ${theme.sz.lg};
      max-width: none;
      pointer-events: none;
      opacity: 0.25;
      background-image: url('${$src}');
      background-repeat: no-repeat;
      background-position: right bottom;
      background-size: ${theme.sz.lg} auto;
    }

    html[data-color-scheme='dark'] & {
      filter: invert(1) hue-rotate(180deg);
    }

    @media (min-width: calc(${theme.sz.maxMobileBreakpoint} + 0.01rem)) and (max-width: ${
        theme.sz.xxl
      }) {
      width: ${theme.sz.md};
      height: ${theme.sz.md};
      background-size: ${theme.sz.md} auto;
    }
  `,
);

export const PlayIcon = styled(Icon)(
  ({ theme }) => css`
    font-size: ${theme.sz.s4};
  `,
);

export const LearnButton = styled(Button)(
  ({ theme }) => css`
    position: relative;
    z-index: 2;
    justify-content: center;
    gap: ${theme.sz.s3};
    border-radius: ${theme.bo.round};
    box-shadow: 0 ${theme.sz.s2} ${theme.sz.s5} ${theme.co.neutral10}12;

    ${ButtonContent} {
      flex-grow: 0;
      padding: 0;
    }
  `,
);

export const State = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s4};
    min-height: 100%;
    padding: ${theme.sz.s8};
    text-align: center;

    h2 {
      ${theme.typography.heading.mobile.s};
      margin: 0;
    }

    p {
      ${theme.typography.paragraph.base};
      max-width: ${theme.sz.s};
      margin: 0;
      color: ${theme.co.neutral40};
    }
  `,
);

export const StateIcon = styled(Icon)(
  ({ theme }) => css`
    color: ${theme.co.primary0};
    font-size: ${theme.sz.s12};
  `,
);

export const LoadingSpinner = styled(Loader)(
  ({ theme }) => css`
    height: ${theme.sz.s10};
  `,
);
